import { Component, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContractRestService } from '../../core/api/contract-rest.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageRequest, PageResponse, Template } from '../../core/models/template.model';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { AccountRestService } from '../../core/api/account-rest.service';
import { Account } from '../../core/models/account.model';
import { firstValueFrom, from, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from '../../core/services/auth.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ContractService } from '../../core/services/contract.service';
import { PdfService } from '../../core/services/pdf.service';

@Component({
  selector: 'app-new-contract',
  imports: [ReactiveFormsModule,
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  templateUrl: './new-contract.component.html',
  styleUrl: './new-contract.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class NewContractComponent {

  private fb = inject(FormBuilder);
  private router = inject(Router);
  contractRestService: ContractRestService = inject(ContractRestService);
  accountRestService: AccountRestService = inject(AccountRestService);
  contractService: ContractService = inject(ContractService);
  private pdfService = inject(PdfService);

  private destroyRef = inject(DestroyRef);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  public readonly userProfile = this.authService.userProfile;

  pageRequest = signal<PageRequest>({
    page: 0,
    size: 10,
    sort: ['createdAt,desc']
  });

  accountRequest = signal<PageRequest>({
    page: 0,
    size: 10,
    sort: ['idf,desc']
  });


  templates: any = signal<PageResponse<Template> | undefined>(undefined);
  idfs: any = signal<PageResponse<Account> | undefined>(undefined);
  loading = signal<boolean>(false);
  preparingContract = signal<boolean>(false);
  exportingPdf = signal(false);
  error: any = signal<Error | undefined>(undefined);
  preparedContractPages: any[] = [];
  preparedContract: any[] = [];
  selectedTemplate: any = signal<Template | undefined>(undefined);
  private preparationGeneration = 0;
  private preparedMetadata?: {accountId: string; title: string};
  private templateGeneration = 0;
  private accountGeneration = 0;
  private pendingLoads = 0;
  private restoredForm = false;

  constractForm = this.fb.group({
    template: this.fb.control<Template | string | null>(null, Validators.required),
    company: [''],
    idf: this.fb.control<Account | string | null>(null, Validators.required),
    validFrom: this.fb.control<Date | string | null>(''),
    validUntil: this.fb.control<Date | string | null>('')
  });

  companies: any = [
    {name: 'Phoenix', value: 'PHOENIX'},
    {name: 'Hageda', value: 'HAGEDA'}
  ]

  contracts: any[] = [];

  constructor() {
    this.constractForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.preparationGeneration++;
      this.preparedContractPages = [];
      this.preparedMetadata = undefined;
      this.selectedTemplate.set(undefined);
      this.preparingContract.set(false);
    });
    effect(() => {
      this.loadTemplates(this.pageRequest());
      this.loadAccounts(this.accountRequest());
    });
  }


  private async loadTemplates(request: PageRequest, search?: any, company?: any): Promise<void> {
    const generation = ++this.templateGeneration;
    this.pendingLoads++;
    this.loading.set(true);
    this.error.set(undefined);

    try {
      const response = await this.contractRestService.getAllTemplates(request, search, company);
      if (!this.destroyRef.destroyed && generation === this.templateGeneration) this.templates.set(response);
    } catch (err) {
      if (!this.destroyRef.destroyed && generation === this.templateGeneration) this.error.set(err as Error);
    } finally {
      this.pendingLoads--;
      if (!this.destroyRef.destroyed) this.loading.set(this.pendingLoads > 0);
    }
  }

  private async loadAccounts(request: PageRequest, search?: string, company?: string) {
    const generation = ++this.accountGeneration;
    this.pendingLoads++;
    this.loading.set(true);
    this.error.set(undefined);

    try {
      const response = await this.accountRestService.getAccounts(request, search, company);
      if (!this.destroyRef.destroyed && generation === this.accountGeneration) {
        this.idfs.set(response);
        if (!this.restoredForm) {
          this.loadExistingData();
          this.restoredForm = true;
        }
      }
    } catch (err) {
      if (!this.destroyRef.destroyed && generation === this.accountGeneration) this.error.set(err as Error);
    } finally {
      this.pendingLoads--;
      if (!this.destroyRef.destroyed) this.loading.set(this.pendingLoads > 0);
    }
  }

  private loadExistingData() {
    const account: string | null = sessionStorage.getItem('account');
    const valid_from: string | null = sessionStorage.getItem('valid_from');
    const date = valid_from ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(valid_from) ? `${valid_from}T00:00:00` : valid_from) : undefined;
    const values: any = {
      idf: account ? JSON.parse(account) : '',
      validFrom: date && !Number.isNaN(date.getTime()) ? date : ''
    };

    this.constractForm.patchValue(values);
  }

  searchTemplates($event: any) {
    console.log('search temps');
    const search: any = this.constractForm.value.template;
    console.warn(search);

    if (search && search?.length >= 3) {
      console.warn("search length reached");
      this.loadTemplates(this.pageRequest(), search, this.constractForm.value.company)
    }

  }

  searchAccounts($event: any) {
    console.log('search accounts');
    let search = this.constractForm.value.idf;
    let company = this.constractForm.value.company;

    if (search === null) {
      search = undefined;
      return;
    }

    if (typeof search === 'string') this.loadAccounts(this.accountRequest(), search);
  }

  prepareContract() {
    if (!this.constractForm.valid || this.preparingContract() || this.exportingPdf()) {
      return;
    }
    const generation = ++this.preparationGeneration;
    this.preparedContractPages = [];
    this.preparedMetadata = undefined;
    this.selectedTemplate.set(undefined);
    this.preparingContract.set(true);
    const data: any = structuredClone(this.constractForm.value);
    if (!data.template?.id || !data.idf?.idf) {
      this.preparingContract.set(false);
      this.snackBar.open('Bitte wählen Sie eine Vorlage und eine Apotheke aus.', 'Schließen', {duration: 5000});
      return;
    }

    from(this.contractRestService.getTemplateById(data.template.id))
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response: Template) => {
        if (generation !== this.preparationGeneration) {
          return;
        }
        this.selectedTemplate.set(response);
        void this.preparePages(response, data, generation);
      },
      error: (error: any) => {
        if (generation !== this.preparationGeneration) {
          return;
        }
        this.preparingContract.set(false);
        console.error('❌ Error getting template:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);

        let errorMessage = 'Fehler beim Laden der Vorlage';

        if (error.status === 401) {
          errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
        } else if (error.status === 403) {
          errorMessage = 'Keine Berechtigung zum Laden von Vorlagen.';
        } else if (error.status === 400) {
          errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
        } else if (error.status === 0) {
          errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.snackBar.open(errorMessage, 'Schließen', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });

  }

  displayTemplate(contract: any): string {
    return contract && contract.name ? contract.name : '';
  }

  displayAccount(account: any): string {
    return account && account.name ? account.idf + ' ' + account.name : '';
  }

  private async preparePages(template: Template, data: any, generation: number) {
    try {
      if (!template.pages?.length) throw new Error('The contract template has no pages.');
      const preparedPages = await Promise.all([...(template.pages ?? [])].sort((a, b) => a.sort_index - b.sort_index).map((page: any, index) =>
        this.preparePage({...page, sort_index: index}, template, data.idf, data.company, template.pages?.length ?? 0, data.validFrom, data.validUntil)
      ));
      if (!this.destroyRef.destroyed && generation === this.preparationGeneration) {
        this.preparedContractPages = preparedPages;
        this.preparedMetadata = {accountId: String(data.idf.idf), title: template.name};
      }
    } catch (error) {
      if (this.destroyRef.destroyed || generation !== this.preparationGeneration) {
        return;
      }
      console.error('Could not prepare contract pages for PDF preview:', error);
      this.snackBar.open('Die Vertragsseiten konnten nicht vollständig vorbereitet werden.', 'Schließen', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    } finally {
      if (!this.destroyRef.destroyed && generation === this.preparationGeneration) {
        this.preparingContract.set(false);
      }
    }
  }

  private async preparePage(page: any, template: Template, account: any, company: string, count: number, validFrom: any, validUntil: any) {
    if (!account) {
      throw new Error('Account details are required to prepare a contract page.');
    }

    page = this.pdfService.preparePage(page, account, {...template, company: company || template.company, validFrom, validUntil}, count);
    const logoCompany = (company || template.company || 'PHOENIX').toLowerCase();
    const parser = new DOMParser();
    const header = parser.parseFromString(page.header_html, 'text/html');
    const body = parser.parseFromString(page.body_html, 'text/html');
    const images = [...header.querySelectorAll('img'), ...body.querySelectorAll('img')];
    const sources = new Set<string>();
    images.forEach(image => {
      const src = image.getAttribute('src');
      if (src && !src.startsWith('data:')) sources.add(src);
    });
    await Promise.all([...sources].map(async source => {
      const actualSource = source === 'phoenix-logo.png' ? `${logoCompany}-logo.png` : source;
      const image = await this.loadImageDataUrl(this.contractRestService.loadImage(actualSource));
      images.filter(element => element.getAttribute('src') === source)
        .forEach(element => element.setAttribute('src', image));
    }));
    page.header_html = header.body.innerHTML;
    page.body_html = body.body.innerHTML;
    return page;
  }

  private async loadImageDataUrl(request: Observable<Blob>): Promise<string> {
    const blob = await firstValueFrom(request);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Could not convert a contract image to a data URL.'));
      reader.onerror = () => reject(reader.error ?? new Error('Could not read a contract image.'));
      reader.readAsDataURL(blob);
    });
  }

  prepareFooter(type: any, pagenr: number, total: number) {
    return type.replaceAll('[page_nr]', pagenr).replaceAll('[total]', total)
  }

  async saveContract() {
    const metadata = this.preparedMetadata;
    if (this.preparingContract() || this.exportingPdf() || this.preparedContractPages.length === 0 || !metadata) {
      return;
    }
    this.exportingPdf.set(true);
    try {
      await this.contractService.contractPdf(this.preparedContractPages, metadata.title, metadata.accountId);
    } catch (error) {
      console.error('Could not create the contract preview PDF:', error);
      this.snackBar.open('Die Vertragsvorschau konnte nicht als PDF erstellt werden.', 'Schließen', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    } finally {
      this.exportingPdf.set(false);
    }
  }

  addToCart() {
    if (this.constractForm.invalid) {
      return;
    }
    // let saved = this.contractService.selectedContracts();
    let saved: any[] = [];
    if (sessionStorage.getItem('selectedContracts') !== null) {
      const current = sessionStorage.getItem('selectedContracts');
      if (current !== null) {
        saved = JSON.parse(current);
      }
    }

    const data: any = this.constractForm.value;
    console.log(data);
    if (!data.template?.id || !data.idf?.id) {
      this.snackBar.open('Bitte wählen Sie eine Vorlage und eine Apotheke aus.', 'Schließen', {duration: 5000});
      return;
    }
    if (saved.some(item => item.accountId != null && String(item.accountId) !== String(data.idf.id))) {
      this.snackBar.open('Der Warenkorb enthält Verträge für eine andere Apotheke. Bitte leeren Sie ihn zuerst.',
        'Schließen', {duration: 5000});
      return;
    }

    let selected = {
      name: data.template.name,
      description: data.template.description ?? '',
      templateId: data.template.id,
      accountId: data.idf.id,
      valid_from: this.dateToIsoDate(data.validFrom),
      valid_until: this.dateToIsoDate(data.validUntil),
      price: data.template.price ?? 0,
      monthly: data.template.monthly ?? 0,
      pos: saved.length + 1
    }

    saved.push(selected);

    sessionStorage.setItem("selectedContracts", JSON.stringify(saved));
    sessionStorage.setItem("account", JSON.stringify(data.idf));
    sessionStorage.setItem("valid_from", selected.valid_from);
    this.contractService.selectedContracts.set(saved);
    this.contractService.cartChanged.update(version => version + 1);
    this.router.navigate(['/cart']);
  }

  private dateToIsoDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(date.getTime()) ? '' :
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
